import { ContentObject } from '../../base';
import { createScores } from '../../base/scoreHelper';

export const mondayCaseStudy: ContentObject = {
    id: 'monday-com-genai-efficiency',
    kind: 'system',
    type: 'case-study',
    name: 'GenAI Optimizes Coding Efficiency & Cuts Development Time at Monday.com',
    description:
        'Monday.com uses GenAI tools like Cursor-AI and pull request analysis to enhance coding efficiency by 25%, streamlining routine tasks and allowing developers to focus on innovation.',
    url: null, // Private interview
    scores: createScores({
        detailedMetrics: 8.0,
        relevanceToUseCase: 8.0,
        reliabilityOfSource: 10.0,
        strategicAndTacticalInformation: 7.0,
        technicalImplementationDetails: 9.0,
        noVendorBias: 10.0,
        relevanceToClient: 7.0,
    }),
    publishedAt: new Date(Date.now() - 9 * 24 * 60 * 60 * 1000), // 9 days ago
    markdown: `# Case Study: Enhancing Developer Productivity with GenAI at Monday.com
 
 This case study explores how Monday.com is leveraging generative AI (GenAI) tools to automate "keep the lights on" tasks and boost overall development efficiency. The insights come from a conversation with a Senior Automation Engineer at Monday.com, who shared their experience integrating AI tools into daily workflows.
 
 ---
 
 ## 1. Source of Information
 
 **Who the Source Is:** 
 The insights are drawn from a conversation with a Senior Automation Engineer at Monday.com. The interview, conducted on February 26, 2025, focused on automating routine tasks and improving developer productivity using GenAI. The engineer's role involves enabling users to build automations on boards and workspaces while monitoring post-production anomalies and facilitating code quality reviews.
 
 **Relevant Quote:** 
 > ""My group focuses on the abilities Monday.com gives to users to automate their day-to-day tasks... we build automations over boards and workspaces."" 
 > – *Senior Automation Engineer at Monday.com*
 
 ---
 
 ## 2. Successes
 
 **Automated Code Assistance and Maintenance**
 
 **Cursor-AI for Real-Time Code Writing:** 
 The engineer explained that Monday.com leverages Cursor-AI as the main IDE tool. Cursor-AI assists with auto-completion by detecting initial keywords and inferring the developer's intentions, thereby suggesting the rest of the code. Although not perfect, the suggestions are mostly accurate and significantly speed up the coding process. 
 > ""It detects your first keywords and then suggests an auto-complete for the rest of the code you want to write. The suggestions are mostly very accurate and good."" 
 > – *Senior Automation Engineer at Monday.com*
 
 **Pull Request Analysis and Insights:** 
 A specialized tool is used to scan pull requests, summarize changes, and provide insights on what code modifications might be needed. This tool helps detect potential bugs and suggests areas for improvement, thus streamlining the code review process without overhauling it completely. 
 > ""We have a tool that analyzes our pull requests and gives us insights—summarizing what changed, its impact, and suggesting code changes."" 
 > – *Senior Automation Engineer at Monday.com*
 
 **Strategic Impact on Workflow – Enhanced Coding Efficiency:** 
 By integrating these GenAI tools into their daily operations, the engineer has experienced significant time savings and reduced manual intervention. With AI handling much of the auto-completion and pull-request insights, developers can focus on higher-value tasks while ensuring code quality remains high.
 
 ---
 
 ## 3. Challenges
 
 **Need for Human Oversight in Auto-Completion:** 
 While Cursor-AI significantly accelerates coding, the engineer notes that its auto-complete suggestions sometimes require manual adjustments. The tool's recommendations aren't always perfectly aligned with the desired output, necessitating a review process through pull requests. 
 > ""I use it a lot, but sometimes I need to change the suggestions because they aren't as accurate as I want them to be."" 
 > – *Senior Automation Engineer at Monday.com*
 
 **Partial Automation in Post-Production Monitoring:** 
 The engineer mentioned that although anomaly detection is in place for post-production deployments (triggering rollbacks when necessary), there isn't a fully automated end-to-end solution yet. This gap means that while AI aids in monitoring, the full rollback process remains partially manual. 
 > ""I'm not sure we have a full end-to-end automation process using GenAI for anomaly detection, but we do monitor failures and roll back versions when needed."" 
 > – *Senior Automation Engineer at Monday.com*
 
 ---
 
 ## 4. Technical Details
 
 **Tools and Integrations:** 
 Monday.com utilizes several GenAI-driven tools in its development workflow:
 - **Cursor-AI:** Integrated into the IDE to provide auto-complete and coding assistance by analyzing the initial keywords and understanding code context.
 - **Pull Request Analysis Tool:** Scans and summarizes pull requests, offering insights on code changes and potential bug detections.
 - **ChatGPT for SQL Queries:** Used by the engineer for generating complex SQL queries when needed.
 
 **Workflow Enhancements:** 
 These tools are incorporated within the development environment to streamline routine tasks. The auto-complete feature provided by Cursor-AI allows for faster code writing, while the pull request analysis tool helps maintain code quality by summarizing changes and detecting issues.
 
 **Relevant Quote:** 
 > ""Having a model running inside our repositories gives us as developers the ability to ask questions without taking the time of an actual person, which is awesome."" 
 > – *Senior Automation Engineer at Monday.com*
 
 ---
 
 ## 5. Results
 
 **Efficiency Gains and Time Savings:** 
 The integration of Cursor-AI has resulted in a measurable enhancement in coding speed. The engineer estimates that these AI tools contribute to approximately a 25% increase in coding efficiency. This time savings allows developers to allocate more effort toward strategic improvements rather than routine coding tasks.
 
 **Impact on Code Review and Quality:** 
 While AI tools streamline the coding phase, the existing pull request approval process ensures that code quality is maintained. The balance between automated suggestions and human review has kept the review process stable, with fewer errors making it to production.
 
 **Overall Workflow Improvements:** 
 By automating portions of the development process—such as auto-completion and pull request analysis—developers at Monday.com can focus on innovation. Although not all processes are fully automated, the tools in play have substantially reduced manual coding time and improved overall productivity.
 
 **Relevant Quotes:** 
 > ""It enhances my time with 25% – I use it a lot, but still sometimes I need to adjust the auto-completion."" 
 > – *Senior Automation Engineer at Monday.com*
 
 > ""The auto-completion enhances my code writing substantially, allowing me to ask questions directly in the repository."" 
 > – *Senior Automation Engineer at Monday.com*
 
 ---
 
 ## 6. Vendors Used
 
 **Primary GenAI Tools:** 
 Monday.com relies on a combination of proprietary and third-party AI solutions:
 - **Cursor-AI:** The main tool integrated into the IDE for auto-completion and coding assistance.
 - **Pull Request Analysis Tool:** A custom solution that scans and provides insights into pull requests.
 - **ChatGPT:** Utilized for generating complex SQL queries and assisting with code documentation when needed.
 
 **Market Perspective on Vendor Landscape:** 
 The conversation did not delve into additional vendor specifics, but the focus remains on how these tools integrate into existing workflows rather than a broad evaluation of multiple vendor solutions.
 
 **Relevant Quote:** 
 > ""I think it's a very powerful tool. The auto-completion and insights really add significant value to our process."" 
 > – *Senior Automation Engineer at Monday.com*
 
 ---
 
 This case study demonstrates that Monday.com's strategic use of GenAI tools—especially Cursor-AI and pull request analysis—has significantly enhanced coding efficiency, contributing to a 25% time improvement. Although there remain areas where human oversight is necessary, the integration of AI-driven solutions has streamlined routine tasks and allowed developers to focus on innovation and strategic improvements.`,
    references: [
        {
            id: '1',
            name: 'Private Varos Interview with Monday.com Engineer',
            author: 'Varos Inc.',
            year: '2025',
            publisher: 'Varos Inc.',
            url: null, // Private interview
        },
    ],
};
